import i18n from "@/i18n";
import { ApiCallError, type SpaceKind } from "@/api";
import { errorMessage } from "@/shared/utils/errors";
import {
	memberLimitFromError,
	memberLimitWords,
	restrictionRefusalWords,
	type MemberLimitAudience,
	type Words,
} from "@/shared/utils/spaceRestriction";

/**
 * The codes a refused move, a refused department access, a refused create-in-a-department and a
 * refused add-from-the-department's-members come with, which the pages word themselves — in terms of what to do instead, rather than in the
 * server's English. Branching is on the `code`, never on the message. Each is a key under
 * `spaces.errors`.
 *
 * `role_in_use` and `invalid_role` also mean other things on other pages (a system role somebody
 * holds, a department's own roles), and those pages word them for themselves; here they are what a
 * *space's* roles and its department's mapping do with them.
 */
const SPACE_ERROR_CODES = new Set([
	"mapping_locked",
	"not_in_department",
	"role_in_use",
	"space_needs_admin",
	"invalid_role",
	"duplicate_mapping",
	"no_spaces",
	"role_required",
	"space_not_found",
	"missing_entitlement",
	"invitation_required",
	"not_a_department_member",
]);

/**
 * The refusals whose server message names the space that caused it — a move is all or nothing, and
 * these are the ones that point at one of the spaces asked for. The words say what happened; the
 * server's sentence is kept beside them, in its own language, for which space it was.
 */
const NAMES_THE_SPACE = new Set([
	"space_needs_admin",
	"not_in_department",
	"space_not_found",
	"missing_entitlement",
]);

/** The i18n key for a refusal's `code`, or `null` when the page has no words for it. */
export function spaceErrorKey(code: string | undefined): string | null {
	return code && SPACE_ERROR_CODES.has(code) ? `spaces.errors.${code}` : null;
}

/** Whether the server's message for `code` is worth showing beside ours: it names the space. */
export function namesTheSpace(code: string | undefined): boolean {
	return !!code && NAMES_THE_SPACE.has(code);
}

/** `words` in the current language: the message with its parameters, and its plural form when it has one. */
export function sayWords(words: Words): string {
	const named = words.params ?? {};
	return words.plural === undefined
		? i18n.global.t(words.key, named)
		: i18n.global.t(words.key, named, words.plural);
}

/** What a space is called, for a refusal that names one of several (`kind` and `id` from its `details`). */
export type SpaceNamer = (kind: SpaceKind, id: string) => string | undefined;

/**
 * The message for a refusal that comes of what a license allows, or `null` for any other error:
 * a personal space that has no room for one more member (with the numbers, said as sentences), a
 * personal space the license does not allow, a Designer that is closed, a space that is locked.
 * For the dialogs that add, invite, accept, move and create; `spaceError` includes it.
 *
 * A move of several spaces to personal names the one that has too many in `details`; `nameOf`, from
 * the dialog that lists them, says which it is called. Somebody accepting an invitation is
 * `invitee`: they cannot make room, only ask.
 */
export function restrictionError(
	e: unknown,
	options: { nameOf?: SpaceNamer; audience?: MemberLimitAudience } = {},
): string | null {
	if (!(e instanceof ApiCallError)) return null;
	const { nameOf, audience } = options;
	const limit = memberLimitFromError(e);
	if (limit) {
		const message = memberLimitWords(limit, audience).map(sayWords).join(" ");
		const name = limit.kind && limit.id ? nameOf?.(limit.kind, limit.id) : undefined;
		return name
			? i18n.global.t("spaceRestriction.errors.memberLimit.named", { name, message })
			: message;
	}
	const refusal = restrictionRefusalWords(e);
	return refusal ? sayWords(refusal) : null;
}

/** The message for a refused request about a space's place: ours for a code we know, else whatever came. */
export function spaceError(e: unknown, nameOf?: SpaceNamer): string {
	const restricted = restrictionError(e, { nameOf });
	if (restricted) return restricted;
	if (e instanceof ApiCallError) {
		const key = spaceErrorKey(e.code);
		if (key) {
			const words = i18n.global.t(key);
			return namesTheSpace(e.code) && e.message ? `${words} (${e.message})` : words;
		}
	}
	return errorMessage(e);
}
